import { z } from 'zod';

/**
 * Contratos de sessão: refresh token, logout e a lista de sessões ativas.
 *
 * O refresh token é opaco (32 bytes aleatórios em base64url = 43 caracteres) e de
 * USO ÚNICO: cada `POST /api/auth/refresh` consome o token apresentado e devolve
 * um par novo. Apresentar de novo um token já consumido é tratado como roubo e
 * derruba a sessão inteira.
 */

/** Header com o identificador de instalação/navegador que amarra a sessão ao dispositivo. */
export const CLIENT_ID_HEADER = 'x-client-id';

/**
 * Header com que o web pede, no `step1` e no `otp/verify`, a sessão em cookie `HttpOnly`
 * (`SESSION_TRANSPORT_COOKIE`). Sem ele a sessão é `bearer`: o token volta no corpo, como o app
 * espera. Ver docs/features/httponly-session-cookie.md.
 */
export const SESSION_TRANSPORT_HEADER = 'x-session-transport';
export const SESSION_TRANSPORT_COOKIE = 'cookie';

/** Como o token de uma sessão viaja. A sessão só aceita o próprio caminho. */
export type SessionTransport = 'bearer' | 'cookie';

/** Cookie `HttpOnly` com o token de sessão do web. */
export const SESSION_COOKIE_NAME = 'fw_session';
/** Prefixo do cookie `HttpOnly` de dispositivo confiável do 2FA, um por usuário (`fw_td_<k>`). */
export const TRUSTED_DEVICE_COOKIE_PREFIX = 'fw_td_';

/**
 * O que o login do web recebe no lugar do token: só o vencimento e o id da sessão. O id não é
 * segredo (sem o cookie não abre nada); serve para a aba saber que outra aba trocou a sessão.
 */
export const CookieSessionInfoSchema = z.object({
	session_id: z.string(),
	token_expires_at: z.string()
});
export type CookieSessionInfo = z.infer<typeof CookieSessionInfoSchema>;

/** Formato aceito para o `X-Client-Id`: 16 a 200 caracteres sem espaço. */
export const ClientIdSchema = z
	.string()
	.min(16)
	.max(200)
	.regex(/^[A-Za-z0-9._~-]+$/);

// ─── POST /api/auth/refresh ──────────────────────────────────────────────────
export const RefreshSessionBodySchema = z
	.object({
		refresh_token: z.string().regex(/^[A-Za-z0-9_-]{43}$/, 'Refresh token inválido')
	})
	.strip();
export type RefreshSessionBody = z.infer<typeof RefreshSessionBodySchema>;

/** Campos de sessão devolvidos no login e no refresh. Os de refresh só vêm quando a sessão tem refresh. */
export const SessionTokensSchema = z.object({
	token: z.string(),
	token_expires_at: z.string(),
	refresh_token: z.string().optional(),
	refresh_token_expires_at: z.string().optional()
});
export type SessionTokens = z.infer<typeof SessionTokensSchema>;

export const RefreshSessionResponseSchema = z.object({
	status: z.literal('success'),
	data: SessionTokensSchema.extend({ refresh_token: z.string(), refresh_token_expires_at: z.string() })
});
export type RefreshSessionResponse = z.infer<typeof RefreshSessionResponseSchema>;

// ─── GET /api/auth/sessions ──────────────────────────────────────────────────
export const ActiveSessionSchema = z.object({
	id: z.string().uuid(),
	device_name: z.string().nullable(),
	ip_address: z.string().nullable(),
	os: z.string().nullable(),
	browser: z.string().nullable(),
	city: z.string().nullable(),
	country: z.string().nullable(),
	refresh_enabled: z.boolean(),
	created_at: z.string(),
	last_seen_at: z.string(),
	expires_at: z.string(),
	current: z.boolean()
});
export type ActiveSession = z.infer<typeof ActiveSessionSchema>;

export const ListSessionsResponseSchema = z.object({
	status: z.literal('success'),
	data: z.array(ActiveSessionSchema)
});
export type ListSessionsResponse = z.infer<typeof ListSessionsResponseSchema>;

// ─── DELETE /api/auth/sessions/:id ───────────────────────────────────────────
export const RevokeSessionParamsSchema = z
	.object({
		id: z.string().uuid('Sessão inválida')
	})
	.strip();
export type RevokeSessionParams = z.infer<typeof RevokeSessionParamsSchema>;
